// src/pages/Partner/PartnerAttention.jsx

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ArrowLeft,
  RefreshCw,
} from "lucide-react";

import {
  Link,
  useOutletContext,
} from "react-router-dom";

import {
  fetchPartnerAttentionDataset,
  getPartnerAttentionCounts,
} from "../../lib/partner/partnerAttention";


export default function PartnerAttention() {
  const {
    access,
  } = useOutletContext();


  const [
    rows,
    setRows,
  ] = useState([]);


  const [
    filter,
    setFilter,
  ] = useState("all");


  const [
    loading,
    setLoading,
  ] = useState(true);


  const [
    error,
    setError,
  ] = useState("");


  /* ------------------------------------------------------------------------ */
  /* Load                                                                     */
  /* ------------------------------------------------------------------------ */

  const loadData =
    useCallback(
      async () => {
        try {
          setLoading(true);
          setError("");

          const dataset =
            await fetchPartnerAttentionDataset(
              access?.partner_role ||
                ""
            );

          setRows(
            dataset.attentionRows
          );
        } catch (
          loadError
        ) {
          console.error(
            "Partner attention page loading failed:",
            loadError
          );

          setRows([]);

          setError(
            loadError?.message ||
              "Participants needing attention could not be loaded."
          );
        } finally {
          setLoading(false);
        }
      },
      [
        access?.partner_role,
      ]
    );


  useEffect(() => {
    loadData();
  }, [loadData]);


  /* ------------------------------------------------------------------------ */
  /* Counts                                                                   */
  /* ------------------------------------------------------------------------ */

  const counts =
    useMemo(
      () =>
        getPartnerAttentionCounts(
          rows
        ),
      [rows]
    );


  const filteredRows =
    useMemo(
      () =>
        filter === "all"
          ? rows
          : rows.filter(
              (row) =>
                row
                  .attention_status ===
                filter
            ),
      [
        rows,
        filter,
      ]
    );


  return (
    <div className="w-full">

      {/* Back */}

      <Link
        to="/partner"
        className="inline-flex items-center gap-2 text-sm font-semibold text-[#666666] transition hover:text-black"
      >
        <ArrowLeft className="h-4 w-4" />

        Overview
      </Link>


      {/* Main table card */}

      <section className="mt-7 overflow-hidden rounded-[14px] border border-[#D4D4D4] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.035)]">

        {/* Header */}

        <div className="px-5 pb-5 pt-5 sm:px-7 sm:pt-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-[25px] font-black tracking-[-0.035em] text-black sm:text-[29px]">
                Participants needing
                attention
              </h1>

              <p className="mt-1 text-sm font-medium text-[#7A7A7A]">
                {counts.all} participant
                {counts.all === 1
                  ? ""
                  : "s"}{" "}
                currently require
                attention.
              </p>
            </div>


            <button
              type="button"
              onClick={
                loadData
              }
              disabled={loading}
              className="inline-flex min-h-[38px] items-center justify-center self-start rounded-lg border border-[#D6D6D6] bg-white px-3.5 text-xs font-bold text-[#555555] transition hover:border-[#FF914D] disabled:opacity-50"
            >
              <RefreshCw
                className={[
                  "mr-2 h-3.5 w-3.5",

                  loading
                    ? "animate-spin"
                    : "",
                ].join(" ")}
              />

              Refresh
            </button>
          </div>


          {/* Filters */}

          <div className="mt-5 flex flex-wrap gap-2">
            <FilterPill
              active={
                filter === "all"
              }
              label="All"
              count={
                counts.all
              }
              onClick={() =>
                setFilter("all")
              }
            />

            <FilterPill
              active={
                filter ===
                "inactive"
              }
              label="Inactive"
              count={
                counts.inactive
              }
              onClick={() =>
                setFilter(
                  "inactive"
                )
              }
            />

            <FilterPill
              active={
                filter ===
                "watch"
              }
              label="Watch"
              count={
                counts.watch
              }
              onClick={() =>
                setFilter(
                  "watch"
                )
              }
            />

            <FilterPill
              active={
                filter ===
                "stalled"
              }
              label="Stalled"
              count={
                counts.stalled
              }
              onClick={() =>
                setFilter(
                  "stalled"
                )
              }
            />
          </div>
        </div>


        {/* Error */}

        {error && (
          <div className="border-t border-[#D7D7D7] px-6 py-6">
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
              {error}
            </div>
          </div>
        )}


        {/* Loading */}

        {!error &&
          loading && (
            <div className="border-t border-[#D7D7D7] px-6 py-16 text-center">
              <RefreshCw className="mx-auto h-5 w-5 animate-spin text-[#FF914D]" />

              <p className="mt-3 text-sm font-semibold text-[#777777]">
                Loading participants…
              </p>
            </div>
          )}


        {/* Empty */}

        {!error &&
          !loading &&
          filteredRows.length ===
            0 && (
            <div className="border-t border-[#D7D7D7] px-6 py-16 text-center">
              <p className="font-bold text-[#555555]">
                No participants need
                attention in this
                category.
              </p>
            </div>
          )}


        {/* Table */}

        {!error &&
          !loading &&
          filteredRows.length >
            0 && (
            <>
              {/* Desktop */}

              <div className="hidden md:block">
                <div className="grid grid-cols-[1.15fr_1fr_1.35fr_1fr_90px] gap-5 border-y border-[#D7D7D7] bg-[#F4F4F4] px-7 py-4 text-[11px] font-black uppercase tracking-wide text-[#777777]">
                  <span>
                    Participant
                  </span>

                  <span>
                    Cohort
                  </span>

                  <span>
                    Attention reason
                  </span>

                  <span>
                    Facilitator
                  </span>

                  <span />
                </div>


                <div className="divide-y divide-[#E0E0E0]">
                  {filteredRows.map(
                    (row) => (
                      <AttentionTableRow
                        key={
                          row
                            .cohort_learner_id
                        }
                        row={row}
                      />
                    )
                  )}
                </div>
              </div>


              {/* Mobile */}

              <div className="divide-y divide-[#E0E0E0] md:hidden">
                {filteredRows.map(
                  (row) => (
                    <AttentionMobileRow
                      key={
                        row
                          .cohort_learner_id
                      }
                      row={row}
                    />
                  )
                )}
              </div>
            </>
          )}

      </section>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* Filters                                                                    */
/* -------------------------------------------------------------------------- */

function FilterPill({
  active,
  label,
  count,
  onClick,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "inline-flex min-h-[37px] items-center gap-2 rounded-full border px-4 text-xs font-semibold transition",

        active
          ? "border-[#FF914D] bg-[#FF914D] text-white"
          : "border-[#D5D5D5] bg-white text-[#777777] hover:border-[#FF914D]",
      ].join(" ")}
    >
      <span>
        {label}
      </span>

      <span>
        {count}
      </span>
    </button>
  );
}


/* -------------------------------------------------------------------------- */
/* Desktop row                                                                */
/* -------------------------------------------------------------------------- */

function AttentionTableRow({
  row,
}) {
  return (
    <div className="grid min-h-[72px] grid-cols-[1.15fr_1fr_1.35fr_1fr_90px] items-center gap-5 px-7 py-4">

      <div className="min-w-0">
        <p className="truncate text-sm font-bold text-black">
          {row.full_name ||
            row.email ||
            "Participant"}
        </p>

        {row.email && (
          <p className="mt-0.5 truncate text-xs font-medium text-[#888888]">
            {row.email}
          </p>
        )}
      </div>


      <p className="text-sm font-medium text-[#333333]">
        {row.cohort_name}
      </p>


      <AttentionReason
        row={row}
      />


      <p className="text-sm font-medium text-[#333333]">
        {row.facilitator_name}
      </p>


      <Link
        to={`/partner/learners/${row.cohort_learner_id}`}
        className="inline-flex min-h-[35px] items-center justify-center rounded-full bg-black px-4 text-xs font-bold text-white transition hover:bg-[#292929]"
      >
        View
      </Link>

    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* Mobile row                                                                 */
/* -------------------------------------------------------------------------- */

function AttentionMobileRow({
  row,
}) {
  return (
    <article className="p-5">

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-black text-black">
            {row.full_name ||
              row.email ||
              "Participant"}
          </p>

          {row.email && (
            <p className="mt-0.5 truncate text-xs text-[#888888]">
              {row.email}
            </p>
          )}
        </div>

        <Link
          to={`/partner/learners/${row.cohort_learner_id}`}
          className="inline-flex min-h-[34px] flex-none items-center justify-center rounded-full bg-black px-4 text-xs font-bold text-white"
        >
          View
        </Link>
      </div>


      <dl className="mt-4 grid gap-4">
        <div>
          <dt className="text-[10px] font-black uppercase tracking-wide text-[#999999]">
            Cohort
          </dt>

          <dd className="mt-1 text-sm font-semibold text-[#333333]">
            {row.cohort_name}
          </dd>
        </div>


        <div>
          <dt className="text-[10px] font-black uppercase tracking-wide text-[#999999]">
            Attention reason
          </dt>

          <dd className="mt-1">
            <AttentionReason
              row={row}
            />
          </dd>
        </div>


        <div>
          <dt className="text-[10px] font-black uppercase tracking-wide text-[#999999]">
            Facilitator
          </dt>

          <dd className="mt-1 text-sm font-semibold text-[#333333]">
            {row.facilitator_name}
          </dd>
        </div>
      </dl>

    </article>
  );
}


/* -------------------------------------------------------------------------- */
/* Reason                                                                     */
/* -------------------------------------------------------------------------- */

function AttentionReason({
  row,
}) {
  return (
    <div>
      <p className="text-sm font-black text-black">
        {row.attention_label}
      </p>

      <p className="mt-0.5 text-xs font-medium leading-relaxed text-[#555555]">
        {row.attention_detail}
      </p>
    </div>
  );
}